/**
 * TaskSelector - Dropdown to select a task to link with a Pomodoro session.
 *
 * Reads available (non-completed) tasks from useTaskStore and presents them
 * in a select-style dropdown. When a task is selected, it passes the task ID
 * and title to the parent (usually used before starting a Pomodoro session).
 *
 * @see Requirements 7.4
 */

import { useEffect, useState, useRef, useCallback } from 'react';
import { useTaskStore } from '@/stores';
import { usePomodoroStore } from '@/stores';

// ─── Component ───────────────────────────────────────────────────────────────

export function TaskSelector() {
  const { tasks, loadTasks } = useTaskStore();
  const { linkedTaskId, linkedTaskTitle, phase } = usePomodoroStore();
  const [isOpen, setIsOpen] = useState(false);
  const containerRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);

  // Load tasks on mount
  useEffect(() => {
    void loadTasks();
  }, [loadTasks]);

  // Close dropdown on outside click
  useEffect(() => {
    if (!isOpen) return;
    const handleClickOutside = (e: MouseEvent) => {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);

  // Filter to only non-completed tasks
  const availableTasks = tasks.filter((t) => !t.isCompleted && !t.deleted);

  const handleSelectTask = useCallback(
    (taskId: string | null, taskTitle: string | null) => {
      // We can only link a task when the timer is idle (before starting a session)
      const { phase: currentPhase } = usePomodoroStore.getState();

      // If idle, store the selection for use when startWork is called
      if (currentPhase === 'idle') {
        usePomodoroStore.setState({
          linkedTaskId: taskId,
          linkedTaskTitle: taskTitle,
        });
      }

      setIsOpen(false);
    },
    []
  );

  const handleKeyDown = useCallback(
    (e: React.KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
      } else if (e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen((prev) => !prev);
      }
    },
    []
  );

  // Disable task selection when timer is running
  const isDisabled = phase !== 'idle';

  return (
    <div ref={containerRef} className="relative w-full">
      <label
        id="task-selector-label"
        className="block text-sm font-medium text-text-secondary mb-1.5"
      >
        Link to Task
      </label>

      {/* Trigger button */}
      <button
        type="button"
        role="combobox"
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-labelledby="task-selector-label"
        aria-disabled={isDisabled}
        disabled={isDisabled}
        onClick={() => !isDisabled && setIsOpen((prev) => !prev)}
        onKeyDown={handleKeyDown}
        className={`
          w-full flex items-center justify-between gap-2
          px-3 py-2.5 text-sm text-left
          bg-bg-card border border-border rounded-lg
          transition-colors duration-fast
          focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-focus-ring
          ${isDisabled ? 'opacity-50 cursor-not-allowed' : 'cursor-pointer hover:border-primary'}
        `}
      >
        <span className={linkedTaskTitle ? 'text-text-primary truncate' : 'text-text-secondary'}>
          {linkedTaskTitle ?? 'No task linked'}
        </span>
        <ChevronIcon isOpen={isOpen} />
      </button>

      {/* Dropdown list */}
      {isOpen && !isDisabled && (
        <ul
          ref={listRef}
          role="listbox"
          aria-labelledby="task-selector-label"
          className="
            absolute z-50 mt-1 w-full max-h-48 overflow-y-auto
            bg-bg-card border border-border rounded-lg shadow-modal
            py-1
          "
        >
          {/* No task option */}
          <li
            role="option"
            aria-selected={linkedTaskId === null}
            tabIndex={0}
            onClick={() => handleSelectTask(null, null)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' || e.key === ' ') {
                e.preventDefault();
                handleSelectTask(null, null);
              }
            }}
            className={`
              px-3 py-2 text-sm cursor-pointer
              transition-colors duration-fast
              focus-visible:outline-none focus-visible:bg-hover-bg
              ${linkedTaskId === null ? 'bg-hover-bg text-text-primary font-medium' : 'text-text-secondary hover:bg-hover-bg'}
            `}
          >
            No task linked
          </li>

          {/* Available tasks */}
          {availableTasks.map((task) => (
            <li
              key={task.taskId}
              role="option"
              aria-selected={linkedTaskId === task.taskId}
              tabIndex={0}
              onClick={() => handleSelectTask(task.taskId, task.title)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                  e.preventDefault();
                  handleSelectTask(task.taskId, task.title);
                }
              }}
              className={`
                px-3 py-2 text-sm cursor-pointer truncate
                transition-colors duration-fast
                focus-visible:outline-none focus-visible:bg-hover-bg
                ${linkedTaskId === task.taskId ? 'bg-hover-bg text-text-primary font-medium' : 'text-text-primary hover:bg-hover-bg'}
              `}
            >
              {task.title}
            </li>
          ))}

          {/* Empty state */}
          {availableTasks.length === 0 && (
            <li className="px-3 py-2 text-sm text-text-secondary text-center">
              No tasks available
            </li>
          )}
        </ul>
      )}
    </div>
  );
}

TaskSelector.displayName = 'TaskSelector';

// ─── Icons ───────────────────────────────────────────────────────────────────

function ChevronIcon({ isOpen }: { isOpen: boolean }) {
  return (
    <svg
      width="16"
      height="16"
      viewBox="0 0 16 16"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
      className={`
        flex-shrink-0 text-text-secondary
        transition-transform duration-fast
        ${isOpen ? 'rotate-180' : ''}
      `}
    >
      <path d="M4 6l4 4 4-4" />
    </svg>
  );
}
